/**
 * Navigation Utilities
 * 
 * Intelligent navigation categorization and organization system.
 * Automatically categorizes navigation items for better UX with large navigation sets.
 */

import type { NavigationItem } from "@/hooks/useNavigationItems";

export interface NavigationCategory {
  name: string;
  items: NavigationItem[];
  icon?: string;
}

export interface CategorizedNavigation {
  primary: NavigationItem[]; // Always visible top-level items
  categories: NavigationCategory[]; // Categorized dropdown items
  uncategorized: NavigationItem[]; // Items that don't fit any category
}

/**
 * Smart categorization rules based on common page patterns
 */
const CATEGORY_PATTERNS: Array<{ name: string; keywords: string[]; icon?: string }> = [
  {
    name: "About",
    keywords: ["about", "mission", "vision", "history", "team", "staff", "leadership", "board", "values"],
    icon: "Info"
  },
  {
    name: "Services",
    keywords: ["service", "solution", "offer", "what we do", "capabilities", "expertise"],
    icon: "Briefcase"
  },
  {
    name: "Programs",
    keywords: ["program", "initiative", "project", "activity", "workshop", "class", "course"],
    icon: "BookOpen"
  },
  {
    name: "Resources",
    keywords: ["resource", "library", "guide", "tool", "download", "document", "faq", "help"],
    icon: "FileText"
  },
  {
    name: "News & Media",
    keywords: ["news", "article", "blog", "press", "media", "announcement", "update", "story"],
    icon: "Newspaper"
  },
  {
    name: "Events",
    keywords: ["event", "calendar", "meeting", "conference", "workshop", "seminar", "webinar"],
    icon: "Calendar"
  },
  {
    name: "Gallery",
    keywords: ["gallery", "photo", "image", "picture", "video", "media", "portfolio"],
    icon: "Camera"
  },
  {
    name: "Projects",
    keywords: ["project", "portfolio", "work", "case study", "showcase", "example"],
    icon: "Folder"
  },
  {
    name: "Get Involved",
    keywords: ["volunteer", "join", "participate", "membership", "donate", "support", "contribute"],
    icon: "Handshake"
  },
  {
    name: "Contact",
    keywords: ["contact", "reach", "connect", "location", "office", "address", "phone", "email"],
    icon: "Mail"
  }
];

/**
 * Determine if a navigation item matches a category
 */
function matchesCategory(item: NavigationItem, category: { keywords: string[] }): boolean {
  const itemName = item.name.toLowerCase();
  const itemPath = item.path.toLowerCase().replace(/^\//, '').replace(/-/g, ' ');
  
  return category.keywords.some(keyword => 
    itemName.includes(keyword.toLowerCase()) || 
    itemPath.includes(keyword.toLowerCase())
  );
}

/**
 * Categorize navigation items intelligently
 * 
 * @param items - All navigation items
 * @param maxPrimaryItems - Maximum number of items to show as primary (default: 7)
 * @returns Categorized navigation structure
 */
export function categorizeNavigation(
  items: NavigationItem[],
  maxPrimaryItems: number = 7
): CategorizedNavigation {
  // Always keep Home as primary
  const homeItem = items.find(item => item.path === '/' || item.name.toLowerCase() === 'home');
  const otherItems = items.filter(item => item.path !== '/' && item.name.toLowerCase() !== 'home');
  
  // If we have few items, return all as primary
  if (items.length <= maxPrimaryItems) {
    return {
      primary: items,
      categories: [],
      uncategorized: []
    };
  }
  
  // Categorize items
  const categorized: Map<string, NavigationItem[]> = new Map();
  const uncategorized: NavigationItem[] = [];
  
  // Initialize categories
  CATEGORY_PATTERNS.forEach(cat => {
    categorized.set(cat.name, []);
  });
  
  // Categorize each item
  otherItems.forEach(item => {
    let matched = false;
    
    for (const category of CATEGORY_PATTERMS) {
      if (matchesCategory(item, category)) {
        const existing = categorized.get(category.name) || [];
        existing.push(item);
        categorized.set(category.name, existing);
        matched = true;
        break;
      }
    }
    
    if (!matched) {
      uncategorized.push(item);
    }
  });
  
  // Build categories array (only include categories with items)
  const categories: NavigationCategory[] = CATEGORY_PATTERNS
    .map(cat => ({
      name: cat.name,
      items: categorized.get(cat.name) || [],
      icon: cat.icon
    }))
    .filter(cat => cat.items.length > 0);
  
  // Determine primary items (most important + home)
  const primary: NavigationItem[] = homeItem ? [homeItem] : [];
  
  // Add most important items to primary (based on order)
  const importantItems = otherItems
    .filter(item => {
      // Check if item is in a category with only 1-2 items (show directly)
      const category = categories.find(cat => cat.items.includes(item));
      return !category || category.items.length <= 2;
    })
    .sort((a, b) => a.order - b.order)
    .slice(0, maxPrimaryItems - primary.length);
  
  primary.push(...importantItems);
  
  // Move categorized items out of primary if they're in categories
  const primaryFiltered = primary.filter(item => {
    if (item.path === '/' || item.name.toLowerCase() === 'home') return true;
    const inCategory = categories.some(cat => cat.items.includes(item));
    // Keep in primary if category has 1-2 items, otherwise move to category
    if (inCategory) {
      const category = categories.find(cat => cat.items.includes(item));
      return category ? category.items.length <= 2 : false;
    }
    return true;
  });
  
  // Remove items from categories if they're in primary
  const categoriesFiltered = categories.map(cat => ({
    ...cat,
    items: cat.items.filter(item => !primaryFiltered.includes(item))
  })).filter(cat => cat.items.length > 0);
  
  return {
    primary: primaryFiltered,
    categories: categoriesFiltered,
    uncategorized: uncategorized.filter(item => !primaryFiltered.includes(item))
  };
}

/**
 * Determine optimal navigation style based on number of items
 * 
 * @param itemCount - Number of navigation items
 * @returns Recommended navigation style
 */
export function getOptimalNavigationStyle(itemCount: number): 'top-bar' | 'mega-menu' | 'sidebar-left' {
  if (itemCount <= 7) {
    return 'top-bar'; // Simple horizontal menu
  } else if (itemCount <= 15) {
    return 'mega-menu'; // Mega menu with dropdowns
  } else {
    return 'mega-menu'; // Mega menu with categorized dropdowns (best for large sites)
  }
}

/**
 * Check if navigation should use categorized mode
 * 
 * @param itemCount - Number of navigation items
 * @returns true if categorized navigation should be used
 */
export function shouldUseCategorizedNavigation(itemCount: number): boolean {
  return itemCount > 10;
}
